import { STATUS } from '@datacloud-incubation/frontend-ai-chat-module';
import { Button, BUTTON_TYPE } from '@datacloud-incubation/frontend-common-library';
import Icons from '@datacloud-incubation/frontend-icons-module';
import classNames from 'classnames';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { INIT_PAGINATION } from '@core/constants/pagination.constant';
import useAbcloudzFlowChatSessions from '../hooks/useAbcloudzFlowChatSessions';
import AIChatWrapper from './AIChatWrapper';
import Loading from './Loading';
import WrapperErrorBlock from './WrapperErrorBlock';
import type { TAbcloudzError } from '../models/abcloudzFlowChat.model';
import type { TSessionItem } from '../models/flowChat.model';
import type { TPagination } from '@core/models/pagination.model';
import type { TOptional, TNullable } from '@core/models/utility.model';
import type { TAbcloudzFlowChat } from 'AbcloudzFlowChat/models/abcloudzFlowChat.models';
import type { TStatusMap } from 'AbcloudzFlowChat/models/statusMap.models';
import styles from '../WrapperAbcloudzFlowChat.module.css';

const AbcloudzFlowChat = ({ flowId }: TAbcloudzFlowChat) => {
  const pagination: TPagination = INIT_PAGINATION;
  const AIChatWrapperRef = useRef<{
    clearMessage: () => void;
    setStatus: (value: React.SetStateAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => void;
  }>(null);

  const [status, setStatus] = useState<TStatusMap>('unknown');
  const [isError, setIsError] = useState<TNullable<TAbcloudzError>>(null);
  const [selectedSession, setSelectedSession] = useState<TOptional<TSessionItem>>(undefined);

  const { getSessionsWithAutoCreate, createSession } = useAbcloudzFlowChatSessions();

  const onInitializeSessions = async () => {
    try {
      setIsError(null);
      setStatus('pending');

      await getSessionsWithAutoCreate.mutate(
        { ...pagination, flowId, silent: true },
        {
          onSuccess: ({ data }) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const sessions = data || [];
            const firstSession = sessions[0];

            if (firstSession?.id) {
              setSelectedSession({
                ...firstSession,
                greeting: firstSession.greeting ?? '',
                updatedAt: firstSession.updatedAt ?? ''
              } as TSessionItem);
              setStatus('flowChat');
            } else {
              setStatus('unknown');
            }
          },
          onError: (error) => {
            const errorData = error.response?.data;

            const title = errorData?.error ?? 'Oops! Something went wrong.';
            const errorMessage = errorData?.message ?? '';

            const validationErrors = Object.entries(errorData?.validationErrors ?? {})
              .map(([_, value]) => (Array.isArray(value) ? value.join(', ') : value))
              .join(', ');

            const message = `${errorMessage} ${validationErrors}`.trim();

            setIsError({
              title,
              message:
                message || 'We encountered an unexpected issue while processing your request.'
            });
            setStatus('failed');
          }
        }
      );
    } catch (_error) {
      // setStatus('failed');
    }
  };

  const onSessionCreate = async () => {
    try {
      await createSession.mutate(
        { flowId },
        {
          onSuccess: ({ data }) => {
            setSelectedSession({ id: data.session.id, name: data.session.name });
            AIChatWrapperRef.current?.setStatus(STATUS.succeeded);
          }
        }
      );
    } catch (_error) {
      //
    }
  };

  // ---

  const CreateNewSession = () => {
    const isProcessing = createSession.isPending;

    return (
      <Button
        buttonType={BUTTON_TYPE.secondary}
        disabled={isProcessing}
        onClick={() => {
          void onSessionCreate();
          AIChatWrapperRef.current?.clearMessage();
        }}
      >
        <Icons.IconRefresh />
      </Button>
    );
  };

  const chatAdditionalButtons: ReactNode[] = [<CreateNewSession key='create-new-session' />];

  // ---

  const statusMap: Record<TStatusMap, () => TNullable<React.JSX.Element>> = {
    pending: () => <Loading />,
    failed: () => (
      <WrapperErrorBlock
        error={isError}
        onRetry={() => {
          void onInitializeSessions();
        }}
      />
    ),
    flowChat: () => (
      <div className={classNames(styles.flowPage)}>
        <div className={styles.flowContainer}>
          <div className={styles.flowChatContent}>
            <div className={styles.chatContainer}>
              {!!selectedSession?.id && (
                <AIChatWrapper
                  key={selectedSession.id}
                  ref={AIChatWrapperRef}
                  flowId={flowId}
                  selectedSession={selectedSession}
                  onSessionCreate={onSessionCreate}
                  updateSessionTitle={() => null}
                  additionalActions={chatAdditionalButtons}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    ),
    unknown: () => null,
    unauthorized: () => null
  };

  // ---

  useEffect(() => {
    void onInitializeSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  // ---

  return statusMap[status]();
};

export default AbcloudzFlowChat;
